import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    console.log('🔐 API Request Interceptor:', {
      url: config.url,
      method: config.method,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
    });
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('⚠️ No token found in localStorage for request to:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('❌ API Response Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      errorData: error.response?.data,
      currentPath: window.location.pathname
    });

    if (error.response?.status === 401) {
      // Only redirect to login if we're not already on a public page
      const isPublicPage = ['/login', '/register', '/'].includes(window.location.pathname);

      console.log('🔒 401 Unauthorized received:', {
        isPublicPage,
        willRedirect: !isPublicPage,
        currentPath: window.location.pathname
      });

      if (!isPublicPage) {
        console.error('Authentication error: Token expired or invalid - clearing auth data and redirecting');
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // Import toast dynamically to show error message
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error('Your session has expired. Please log in again.');
        });

        // Small delay to show the toast before redirecting
        setTimeout(() => {
          window.location.href = '/login';
        }, 500);
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
};

export const petAPI = {
  getPets: () => api.get('/pets'),
  getPet: (id) => api.get(`/pets/${id}`),
  createPet: (petData) => api.post('/pets', petData),
  updatePet: (id, petData) => api.put(`/pets/${id}`, petData),
  deletePet: (id) => api.delete(`/pets/${id}`),
  getPetTypes: () => api.get('/pets/types/all'),
};

export const exerciseAPI = {
  getExercises: (params) => api.get('/exercises', { params }),
  getExercise: (id) => api.get(`/exercises/${id}`),
  getCompatibleExercises: (petId) => api.get(`/exercises/pet/${petId}/compatible`),
  getCategories: () => api.get('/exercises/categories'),
  getDifficulties: () => api.get('/exercises/difficulties'),
};

export const trainingAPI = {
  createSession: (sessionData) => api.post('/training/sessions', sessionData),
  getPetSessions: (petId, params) => api.get(`/training/sessions/pet/${petId}`, { params }),
  getPetProgress: (petId) => api.get(`/training/progress/pet/${petId}`),
  updateProgress: (petId, exerciseId, progressData) => 
    api.put(`/training/progress/pet/${petId}/exercise/${exerciseId}`, progressData),
  getPetStats: (petId) => api.get(`/training/stats/pet/${petId}`),
  getPrograms: (params) => api.get('/training/programs', { params }),
  getProgram: (id) => api.get(`/training/programs/${id}`),
};

export const aiVetAPI = {
  getSessions: () => api.get('/ai-vet/sessions'),
  getSession: (id) => api.get(`/ai-vet/sessions/${id}`),
  createSession: (sessionData) => api.post('/ai-vet/sessions', sessionData),
  updateSession: (id, updateData) => api.put(`/ai-vet/sessions/${id}`, updateData),
  deleteSession: (id) => api.delete(`/ai-vet/sessions/${id}`),
  sendMessage: (sessionId, messageData) => api.post(`/ai-vet/sessions/${sessionId}/messages`, messageData),
  getRecommendations: (params) => api.get('/ai-vet/recommendations', { params }),
  updateRecommendation: (id, updateData) => api.put(`/ai-vet/recommendations/${id}`, updateData),
};

// Default export for components that import api directly
export default api;